import { useEffect, useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { AlertCircle, Settings, Pencil } from 'lucide-react'
import { BuilderProvider } from './BuilderContext'
import { BuilderCanvas } from './BuilderCanvas'
import { applyContextMapping } from './editable/contextFields'
import { setPublicRenderMode } from '../../services/fetchPatch'
// Boot the editor plugin registry — even in read-only render, blocks may
// reference primitives that come from the same boot file.
import './editors'
import { CornerDockSlot } from '../shell/CornerDock';
import { DOCK_ORDER } from '../shell/cornerDock';

/**
 * Rendu public d'une URL de page builder.
 *
 * Appelle `/api/builder/resolve?path=<location.pathname>` pour résoudre la
 * route en l'un des deux cas :
 *
 *  - `kind: 'static'`   — page sans data-binding, accessible à son slug
 *                         canonique. Rendu nu.
 *  - `kind: 'redirect'` — aucune page à ce chemin, mais une AUTRE page le
 *                         déclare comme ancienne adresse. Le composant
 *                         saute vers `to`, en `replace`.
 *  - `kind: 'template'` — page-modèle matchée par un de ses `modelFor`.
 *                         L'instance d'objet résolue est passée à
 *                         BuilderCanvas via `objectContext`, ce qui injecte
 *                         `_context_.{field}` dans le store reactive-json.
 *                         Le mapping (renommage de champs object → context)
 *                         est appliqué côté frontend.
 *
 * Mode toujours en lecture seule : ce composant n'est jamais la surface
 * d'édition. L'édition d'une page-modèle se fait via `/builder/{slug}` comme
 * n'importe quelle autre page — un engrenage flottant y mène quand le back a
 * répondu `canEdit` (cf. EditPageShortcut).
 *
 * Un 404 ou un 403 peut lui aussi porter une page : le site déclare, dans sa
 * configuration, la page builder à servir pour chacun de ces statuts (champs
 * `page_404` / `page_403` du groupe). Le back la renvoie alors dans le corps de
 * la réponse d'erreur, avec `errorStatus`, et elle se rend comme une page
 * statique ordinaire — même canevas, mêmes blocs, aucun cas particulier ici
 * hors du <head> (pas de canonical sur une URL qui n'existe pas). Un site qui
 * n'en déclare pas garde l'écran nu ci-dessous.
 *
 * Props :
 *  - fallback : ReactNode rendu si l'endpoint /api/builder/resolve renvoie
 *    404 SANS page d'erreur déclarée. Permet d'enchaîner avec un autre routeur
 *    (DynamicPageRouter, NotFoundPage…) sans dupliquer la logique de routage.
 *    Si omis, un "not found" minimaliste est affiché.
 */
export const BuilderPublicRenderer = ({ fallback }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [resolved, setResolved] = useState(null)
  // Le chemin POUR LEQUEL `resolved` a été obtenu. Indispensable dès qu'une
  // résolution peut faire NAVIGUER : `location.pathname` a alors déjà changé
  // quand l'état, lui, décrit encore le chemin d'avant.
  const [resolvedFor, setResolvedFor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    setError(null)
    setResolved(null)
    const requestedPath = location.pathname
    setResolvedFor(null)

    fetch(`/api/builder/resolve?path=${encodeURIComponent(location.pathname)}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        // 404 et 403 ne sont pas de simples échecs : quand le site déclare une
        // page d'erreur (champs `page_404` / `page_403` du groupe), le back la
        // renvoie DANS la réponse d'erreur, avec `errorStatus`. Le corps décide
        // — page dedans, on la rend ; rien dedans, on retombe sur le fallback.
        // Le statut HTTP, lui, reste celui de l'erreur : c'est le comportement
        // correct pour un cache, un moniteur ou un crawler.
        if (res.status === 404 || res.status === 403) {
          const body = await res.json().catch(() => null)
          if (body?.pageSlug) return body
          if (!cancelled) setNotFound(true)
          return null
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        return res.json()
      })
      .then((data) => {
        if (cancelled || !data) return
        setResolved(data)
        setResolvedFor(requestedPath)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e.message || 'Resolution failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [location.pathname])

  // Une ancienne adresse mène ici : le back a répondu `kind: redirect`. Le saut
  // se fait côté SPA — c'est ce que voit le visiteur. Le vrai 301 HTTP, lui, est
  // l'affaire du shell SSR (`SitePublicController::shell`), qui répond avant que
  // ce composant n'existe : les deux couvrent deux publics distincts, le
  // visiteur d'un côté, les moteurs et les moniteurs de l'autre.
  // La résolution appartient au chemin pour lequel elle a été demandée. Sans ce
  // repère, la redirection se mordait la queue : à l'instant où le saut aboutit,
  // `location.pathname` DEVIENT la cible, et la comparer à `to` faisait passer
  // un saut réussi pour une boucle — l'écran retombait alors sur le `fallback`
  // authentifié, qui expédiait le visiteur anonyme vers /login. Vérifié à
  // l'écran : c'est exactement ce qui se produisait.
  const isStale = resolvedFor !== null && resolvedFor !== location.pathname
  const redirectTo = (!isStale && resolved?.kind === 'redirect') ? resolved.to : null
  // Une redirection vers sa PROPRE adresse tournerait sans fin. Le back en
  // écarte déjà une, mais on ne fait pas dépendre l'écran de sa correction :
  // une base héritée d'un import peut en porter une.
  const isRedirectLoop = Boolean(redirectTo) && redirectTo === resolvedFor
  useEffect(() => {
    if (!redirectTo || isRedirectLoop) return
    // `replace` : sans lui, le bouton Retour ramènerait sur l'adresse morte,
    // qui rejouerait aussitôt la redirection — le visiteur serait piégé.
    // La query string du visiteur suit la redirection (utm_*, etc.).
    navigate(redirectTo + location.search, { replace: true })
  }, [redirectTo, isRedirectLoop, location.search, navigate])

  // Une page a été résolue : on rend du public, donc un 401 ne doit plus
  // expédier le visiteur sur le login Drupal. Scopé à la résolution réussie —
  // quand on retombe sur le `fallback` (shell authentifié), la redirection
  // reprend son rôle normal.
  const isRendering = Boolean(resolved?.pageSlug)
  useEffect(() => {
    if (!isRendering) return undefined
    setPublicRenderMode(true)
    return () => setPublicRenderMode(false)
  }, [isRendering])

  // La langue du document est une propriété de l'ENVELOPPE HTML, hors du
  // périmètre d'un rjBuild : aucun YAML ne peut poser <html lang>. Sans elle,
  // les pages anglaises étaient annoncées en français aux lecteurs d'écran et
  // aux moteurs (WCAG 3.1.1).
  useDocumentHead(
    resolved?.pageTitle,
    location.pathname,
    resolved?.langcode,
    resolved?.errorStatus,
    resolved?.siteFavicon?.url,
    resolved?.siteFavicon?.mime,
    resolved?.meta,
  )

  // Le saut part dans un effet : jusque-là on garde le chargement plutôt que
  // d'afficher un écran vide, puis la page d'arrivée, ce qui clignoterait.
  if (loading || isStale || (redirectTo && !isRedirectLoop)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
      </div>
    )
  }

  if (notFound || isRedirectLoop) {
    return fallback ?? <DefaultNotFoundView path={location.pathname} />
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <AlertCircle size={28} className="text-amber-500 mb-3" />
        <p className="text-sm text-gray-600">Erreur de chargement : {error}</p>
      </div>
    )
  }

  if (!resolved?.pageSlug) {
    return fallback ?? <DefaultNotFoundView path={location.pathname} />
  }

  // For template pages, derive the `_context_` payload from the resolved
  // instance + mapping declaration. For static pages, objectContext stays
  // undefined and BuilderCanvas renders without injection.
  const objectContext = resolved.kind === 'template'
    ? applyContextMapping(resolved.instance?.data, resolved.mapping)
    : undefined

  return (
    <BuilderProvider slug={resolved.pageSlug} initialEditMode={false}>
      <div id="builder-public" className="min-h-screen flex flex-col bg-white">
        {/* apiPrefix : sous quel préfixe d'URL rappeler l'API pour obtenir la
            langue résolue (`/en` → contenu anglais). Du transport entre deux
            composants d'orchestration — aucun YAML n'intervient dans ce fetch. */}
        <BuilderCanvas slug={resolved.pageSlug} objectContext={objectContext} apiPrefix={resolved.apiPrefix || ''} alternates={resolved.alternates} />
        {/* Sur une page-modèle, ce qu'on vient corriger est presque toujours le
            CONTENU, pas le gabarit : son raccourci ouvre l'instance dans ce
            modèle-là (`?model=`), donc à l'endroit exact où on vient de la
            lire. Chacun des deux raccourcis dépend de SON droit d'écriture. */}
        {resolved.canEditInstance && resolved.objectType && (resolved.instance?.slug || resolved.instance?.id) && (
          <EditContentShortcut
            objectType={resolved.objectType}
            instanceId={resolved.instance.slug || resolved.instance.id}
            modelSlug={resolved.pageSlug}
          />
        )}
        {resolved.canEdit && <EditPageShortcut slug={resolved.pageSlug} isModel={resolved.kind === 'template'} />}
      </div>
    </BuilderProvider>
  )
}

/**
 * Raccourci « modifier cette page » du rendu public : un engrenage flottant
 * vers `/builder/{slug}`, l'éditeur de cette même page.
 *
 * Affiché UNIQUEMENT sur le `canEdit` renvoyé par `/api/builder/resolve`,
 * càd l'accès `update` de Drupal sur le nœud servi : un visiteur anonyme ou
 * un compte sans droit d'édition reçoit FALSE et ne voit rien. Le front ne
 * devine aucun droit — il affiche ce que le back a autorisé.
 *
 * Icône seule → le nom accessible vient de l'`aria-label` (le `title` ne fait
 * que doubler l'info à la souris) ; l'icône elle-même est décorative.
 */
const EditPageShortcut = ({ slug, isModel = false }) => (
  // La POSITION appartient à la barre du coin, plus au bouton.
  <CornerDockSlot order={DOCK_ORDER.builderPublic}>
    <Link
      to={`/builder/${slug}`}
      // Sur une page-modèle, deux boutons voisins mènent à deux endroits
      // différents : leurs noms accessibles doivent les distinguer, sinon on
      // écoute « Modifier cette page » deux fois sans savoir laquelle.
      aria-label={isModel ? 'Modifier le modèle de cette page dans le builder' : 'Modifier cette page dans le builder'}
      title={isModel ? 'Modifier le modèle' : 'Modifier cette page'}
      className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-teal-700 text-white shadow-lg hover:bg-teal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 print:hidden"
    >
      <Settings size={20} aria-hidden="true" />
    </Link>
  </CornerDockSlot>
)

/**
 * Raccourci « modifier ce contenu » — le pendant du précédent sur une
 * page-modèle : il ouvre l'INSTANCE affichée, dans CE modèle
 * (`/builder/types/<type>/instance/<slug>?model=<page>`), c'est-à-dire à
 * l'endroit exact où on vient de la lire.
 *
 * Affiché sur le seul `canEditInstance` renvoyé par `/api/builder/resolve` :
 * l'accès `update` de Drupal sur l'instance, qui n'a rien à voir avec celui du
 * gabarit — un rédacteur corrige son article sans pouvoir toucher au modèle.
 */
const EditContentShortcut = ({ objectType, instanceId, modelSlug }) => (
  <CornerDockSlot order={DOCK_ORDER.builderContent}>
    <Link
      to={`/builder/types/${objectType}/instance/${instanceId}?model=${encodeURIComponent(modelSlug)}`}
      aria-label="Modifier ce contenu dans le builder"
      title="Modifier ce contenu"
      className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-teal-700 border border-teal-200 shadow-lg hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 print:hidden"
    >
      <Pencil size={20} aria-hidden="true" />
    </Link>
  </CornerDockSlot>
)

/**
 * Pose une balise `<meta>` et renvoie de quoi défaire l'opération : on
 * restaure la valeur précédente si la balise existait déjà (celles de
 * `index.html`), on retire la balise si on vient de la créer.
 */
const setMeta = (attrName, attrValue, content, undo) => {
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`)
  if (el) {
    const previous = el.getAttribute('content')
    undo.push(() => el.setAttribute('content', previous ?? ''))
  } else {
    el = document.createElement('meta')
    el.setAttribute(attrName, attrValue)
    document.head.appendChild(el)
    undo.push(() => el.remove())
  }
  el.setAttribute('content', content)
}

/**
 * Pose la favicone du SITE, et rend de quoi remettre celle de l'application.
 *
 * L'icône n'appartient pas à la page mais au site : elle arrive avec la
 * résolution (`siteFavicon`), qui est de toute façon appelée à chaque
 * navigation publique. Le `<link rel="icon">` d'`index.html` est réutilisé
 * quand il existe — en créer un second laisserait le choix au navigateur, qui
 * garde en pratique le dernier ; on écrase donc celui qui est là.
 *
 * Le shell SSR (`SitePublicController::shell`) pose la MÊME balise dans le HTML
 * servi, comme il le fait déjà pour le titre et les métadonnées : les deux
 * couvrent deux moments distincts — le premier octet pour lui, la navigation
 * dans la SPA pour celle-ci — et ni l'un ni l'autre ne suffit seul. Il ajoute
 * en plus `apple-touch-icon`, qu'iOS ne lit qu'au chargement du document : le
 * poser après coup en JS ne servirait à rien.
 */
const setFavicon = (favicon, undo) => {
  if (!favicon?.url) return
  let el = document.head.querySelector('link[rel="icon"]')
  if (el) {
    const previousHref = el.getAttribute('href')
    const previousType = el.getAttribute('type')
    undo.push(() => {
      el.setAttribute('href', previousHref ?? '')
      if (previousType) el.setAttribute('type', previousType)
      else el.removeAttribute('type')
    })
  } else {
    el = document.createElement('link')
    el.setAttribute('rel', 'icon')
    document.head.appendChild(el)
    undo.push(() => el.remove())
  }
  el.setAttribute('href', favicon.url)
  if (favicon.mime) el.setAttribute('type', favicon.mime)
  else el.removeAttribute('type')
}

const setCanonical = (href, undo) => {
  let el = document.head.querySelector('link[rel="canonical"]')
  if (el) {
    const previous = el.getAttribute('href')
    undo.push(() => el.setAttribute('href', previous ?? ''))
  } else {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
    undo.push(() => el.remove())
  }
  el.setAttribute('href', href)
}

/**
 * Donne son identité à une page builder rendue publiquement : `index.html`
 * porte un `<title>Orbit</title>` figé, donc sans ça toutes les pages publiées
 * partagent le titre de l'application.
 *
 * Tout est restauré au démontage — une navigation vers un écran Orbit ne doit
 * pas garder le titre d'une landing dans l'onglet.
 *
 * LES BALISES SAISIES dans le panneau « Métadonnées » (`meta` : titre,
 * description, image de partage, canonique, robots) arrivent avec la résolution
 * — pour une page comme pour un CONTENU servi par le modèle de son type, dont
 * les balises l'emportent sur celles du gabarit (cf. `matchPath()`). Elles
 * étaient posées par le shell SSR d'orbit_site et par LUI SEUL : une navigation
 * dans la SPA — et tout environnement où le front n'est pas servi par ce shell —
 * gardait la description de la page précédente, ou aucune. Les deux couvrent
 * deux moments distincts (le premier octet là-bas, la navigation ici) et ni
 * l'un ni l'autre ne suffit seul ; ils lisent la même clé et posent les mêmes
 * balises.
 *
 * PORTÉE : agit après le rendu JS, donc suffit pour l'onglet du navigateur et
 * pour Google (qui exécute le JS), mais PAS pour les crawlers d'aperçu de
 * LinkedIn, Facebook, Slack ou WhatsApp, qui lisent le HTML brut — eux sont
 * servis par le shell SSR.
 *
 * Rien n'est posé à vide : une balise sans valeur vaut moins que pas de balise
 * du tout, et tout est défait au démontage.
 */
function useDocumentHead(title, path, langcode, errorStatus, faviconUrl, faviconMime, meta) {
  // Les balises viennent d'un objet reconstruit à chaque résolution : sans
  // cette stabilisation, l'effet se rejouerait à chaque rendu et reposerait le
  // <head> en boucle.
  const metaKey = JSON.stringify(meta ?? {})

  useEffect(() => {
    // La favicone est celle du SITE : elle vaut même pour une page sans titre,
    // et n'a pas à attendre les balises de la page.
    const undo = []
    setFavicon(faviconUrl ? { url: faviconUrl, mime: faviconMime } : null, undo)

    if (!title) {
      return () => undo.forEach((restore) => restore())
    }

    const tags = JSON.parse(metaKey)
    const text = (key) => {
      const value = tags[key]
      return typeof value === 'string' ? value.trim() : ''
    }
    const previousTitle = document.title
    const previousLang = document.documentElement.lang
    const url = window.location.origin + path
    // Un `title` saisi REMPLACE le titre composé, exactement comme côté SSR.
    const headTitle = text('title') || title
    const description = text('description')
    const image = text('image')

    document.title = headTitle
    if (langcode) document.documentElement.lang = langcode
    setMeta('property', 'og:title', headTitle, undo)
    setMeta('property', 'og:type', 'website', undo)
    if (description) {
      setMeta('name', 'description', description, undo)
      setMeta('property', 'og:description', description, undo)
    }
    if (image) {
      // Chemin interne → URL absolue : un aperçu de partage ne résout pas les
      // chemins relatifs.
      const absolute = image.startsWith('/') ? window.location.origin + image : image
      setMeta('property', 'og:image', absolute, undo)
      setMeta('name', 'twitter:card', 'summary_large_image', undo)
    }
    // Sur une page d'erreur, l'URL courante n'existe PAS : la déclarer
    // canonique (ou la donner en `og:url`) reviendrait à faire indexer une
    // adresse morte sous le contenu de la page 404 — pire que de ne rien
    // déclarer. On demande explicitement le contraire.
    if (errorStatus || text('robots') === 'noindex') {
      setMeta('name', 'robots', 'noindex, follow', undo)
    }
    if (!errorStatus) {
      // Une canonique SAISIE désigne la page de référence ailleurs ; sinon
      // c'est l'URL demandée.
      const canonical = text('canonical') || url
      setMeta('property', 'og:url', canonical, undo)
      setCanonical(canonical, undo)
    }

    return () => {
      document.title = previousTitle
      document.documentElement.lang = previousLang
      undo.forEach((restore) => restore())
    }
  }, [title, path, langcode, errorStatus, faviconUrl, faviconMime, metaKey])
}


const DefaultNotFoundView = ({ path }) => (
  <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
    <p className="text-sm text-gray-600">
      Aucune page ne correspond à <code className="font-mono text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">{path}</code>.
    </p>
  </div>
)
